import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users, Search, MapPin, UserPlus, Sparkles, Star, Briefcase, Clock,
  Check, Network, TrendingUp, Compass, Target, Award, ArrowRight, Filter,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const ScrollableRow = ({ children }) => {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    const el = ref.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el?.removeEventListener('scroll', checkScroll);
  }, [children]);

  const scroll = (dir) => {
    ref.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  return (
    <div className="relative group">
      {canLeft && (
        <button
          onClick={() => scroll(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/90 border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-all opacity-0 group-hover:opacity-100"
          data-testid="scroll-left-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border scroll-smooth">
        {children}
      </div>
      {canRight && (
        <button
          onClick={() => scroll(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/90 border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-all opacity-0 group-hover:opacity-100"
          data-testid="scroll-right-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

const HeroCard = ({ pro, onConnect, connecting }) => (
  <Card className="flex-shrink-0 w-72 overflow-hidden hover:shadow-lg transition-all group" data-testid={`pro-${pro.id}`}>
    <div className="h-20 bg-gradient-to-r from-primary/20 via-purple-500/15 to-blue-500/20 relative">
      {pro.is_premium && (
        <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] px-1.5"><Star className="w-2.5 h-2.5 mr-0.5" />PRO</Badge>
      )}
      {pro.is_ambassador && (
        <Badge className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] px-1.5"><Award className="w-2.5 h-2.5 mr-0.5" />Ambassador</Badge>
      )}
    </div>
    <CardContent className="px-5 pb-5 -mt-8 relative">
      <div className="w-16 h-16 rounded-xl border-4 border-background bg-muted overflow-hidden mb-3 shadow-md">
        {pro.profile_photo ? (
          <img src={resolveImageUrl(pro.profile_photo)} alt={pro.full_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-bold text-xl">{(pro.full_name || 'P')[0]}</span>
          </div>
        )}
      </div>
      <h3 className="font-semibold text-sm line-clamp-1 mb-0.5">{pro.full_name}</h3>
      <p className="text-xs text-primary font-medium line-clamp-1">{pro.profession || 'Professional'}</p>
      {pro.company && <p className="text-[11px] text-muted-foreground line-clamp-1">{pro.company}</p>}
      {(pro.city || pro.state) && (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3" />{[pro.city, pro.state].filter(Boolean).join(', ')}
        </p>
      )}
      {pro.insurance_carrier && (
        <p className="text-[10px] text-muted-foreground mt-1">
          {pro.insurance_carrier} &middot; {Array.isArray(pro.insurance_specialty) ? pro.insurance_specialty.join(', ').toUpperCase() : (pro.insurance_specialty || '')}
        </p>
      )}
      {pro.match_score > 0 && (
        <div className="mt-3">
          {pro.match_reasons?.length > 0 && (
            <p className="text-[11px] text-muted-foreground mb-1.5 line-clamp-1">{pro.match_reasons[0]}</p>
          )}
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Match</span>
            <span className="font-semibold text-primary">{pro.match_score}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full" style={{ width: `${pro.match_score}%` }} />
          </div>
        </div>
      )}
      <div className="mt-4">
        <Button
          size="sm"
          variant={pro.is_connected ? 'outline' : 'default'}
          onClick={() => onConnect(pro.id)}
          disabled={connecting === pro.id || pro.is_connected}
          className="w-full text-xs h-8"
          data-testid={`connect-${pro.id}`}
        >
          {pro.is_connected ? (<><Check className="w-3 h-3 mr-1" />Connected</>) :
           connecting === pro.id ? 'Connecting...' :
           (<><UserPlus className="w-3 h-3 mr-1" />Connect</>)}
        </Button>
      </div>
    </CardContent>
  </Card>
);

const SectionRow = ({ title, subtitle, icon: Icon, iconColor, pros, onConnect, connecting, loading, emptyMsg }) => (
  <section className="space-y-4" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2.5">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <h2 className="text-lg font-bold font-['Outfit']">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {pros.length > 0 && <Badge variant="secondary" className="ml-auto text-xs">{pros.length}</Badge>}
    </div>
    {loading ? (
      <div className="flex gap-4 overflow-x-auto pb-2">
        {[1,2,3,4].map(i => <div key={i} className="flex-shrink-0 w-72 h-72 bg-muted rounded-xl animate-pulse" />)}
      </div>
    ) : pros.length > 0 ? (
      <ScrollableRow>
        {pros.map(pro => <HeroCard key={pro.id} pro={pro} onConnect={onConnect} connecting={connecting} />)}
      </ScrollableRow>
    ) : emptyMsg ? (
      <Card>
        <CardContent className="py-8 text-center">
          <Icon className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">{emptyMsg}</p>
        </CardContent>
      </Card>
    ) : null}
  </section>
);

// Featured Members Spotlight Carousel
const FeaturedSpotlight = ({ pros, onConnect, connecting }) => {
  if (pros.length === 0) return null;
  return (
    <section className="space-y-4" data-testid="featured-members-spotlight">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 text-white">
          <Award className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold font-['Outfit']">Featured Members</h2>
          <p className="text-xs text-muted-foreground">Top-performing & newly verified professionals</p>
        </div>
      </div>
      <ScrollableRow>
        {pros.map(pro => (
          <Card key={pro.id} className="flex-shrink-0 w-80 overflow-hidden border-amber-200/60 bg-gradient-to-br from-amber-50/50 to-orange-50/30 hover:shadow-xl transition-all" data-testid={`featured-${pro.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl border-2 border-amber-300 bg-muted overflow-hidden flex-shrink-0 shadow-md">
                  {pro.profile_photo ? (
                    <img src={resolveImageUrl(pro.profile_photo)} alt={pro.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">{(pro.full_name || 'P')[0]}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <h3 className="font-bold text-sm line-clamp-1">{pro.full_name}</h3>
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                    {pro.is_ambassador && <Award className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-primary font-semibold line-clamp-1">{pro.profession || 'Professional'}</p>
                  {pro.company && <p className="text-[11px] text-muted-foreground line-clamp-1">{pro.company}</p>}
                  {(pro.city || pro.state) && (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />{[pro.city, pro.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
              {pro.match_score > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-amber-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: `${pro.match_score}%` }} />
                  </div>
                  <span className="text-xs font-bold text-amber-600">{pro.match_score}%</span>
                </div>
              )}
              <Button
                size="sm"
                variant={pro.is_connected ? 'outline' : 'default'}
                onClick={() => onConnect(pro.id)}
                disabled={connecting === pro.id || pro.is_connected}
                className="w-full mt-3 text-xs h-8"
                data-testid={`featured-connect-${pro.id}`}
              >
                {pro.is_connected ? (<><Check className="w-3 h-3 mr-1" />Connected</>) :
                 connecting === pro.id ? 'Connecting...' :
                 (<><UserPlus className="w-3 h-3 mr-1" />Connect</>)}
              </Button>
            </CardContent>
          </Card>
        ))}
      </ScrollableRow>
    </section>
  );
};

export default function ExplorePage() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [nearYou, setNearYou] = useState([]);
  const [groups, setGroups] = useState([]);
  const [allPros, setAllPros] = useState([]);
  const [localPros, setLocalPros] = useState([]);
  const [localCategories, setLocalCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localLoading, setLocalLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [connecting, setConnecting] = useState(null);

  const CATEGORIES = [
    { id: null, label: 'All', icon: Compass },
    { id: 'realtor', label: 'Realtors', icon: MapPin },
    { id: 'insurance', label: 'Insurance', icon: Briefcase },
    { id: 'contractor', label: 'Contractors', icon: Briefcase },
    { id: 'accountant', label: 'Accountants', icon: Briefcase },
    { id: 'lawyer', label: 'Lawyers', icon: Briefcase },
    { id: 'financial', label: 'Financial', icon: TrendingUp },
    { id: 'loan_officer', label: 'Loan Officers', icon: Briefcase },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [recData, nearData, groupsData, localData] = await Promise.all([
        api.getRecommendedProfessionals(30),
        api.getNearbyProfessionals(30).catch(() => ({ professionals: [] })),
        api.getGroups().catch(() => ({ groups: [] })),
        api.discoverLocal().catch(() => ({ professionals: [], categories: {} })),
      ]);
      const recs = recData.recommended || [];
      const near = nearData.professionals || [];
      setMatches(recs);
      setNearYou(near);
      setGroups(groupsData.groups || []);
      setLocalPros(localData.professionals || []);
      setLocalCategories(localData.categories || {});
      const seen = new Set();
      const all = [];
      for (const p of [...recs, ...near]) {
        if (!seen.has(p.id)) { seen.add(p.id); all.push(p); }
      }
      setAllPros(all);
    } catch (error) { console.error('Failed to fetch professionals:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCategoryFilter = async (catId) => {
    setActiveCategory(catId);
    setLocalLoading(true);
    try {
      const data = await api.discoverLocal(catId);
      setLocalPros(data.professionals || []);
      setLocalCategories(data.categories || {});
    } catch (err) {
      console.error('Failed to filter:', err);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleConnect = async (userId) => {
    setConnecting(userId);
    try {
      await api.sendConnectionRequest(userId);
      const update = m => m.map(p => p.id === userId ? { ...p, is_connected: true } : p);
      setMatches(update);
      setNearYou(update);
      setAllPros(update);
      toast.success('Connection request sent!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to connect');
    } finally { setConnecting(null); }
  };

  const filterBySearch = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(p =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.profession || '').toLowerCase().includes(q) ||
      (p.company || '').toLowerCase().includes(q) ||
      (p.city || '').toLowerCase().includes(q)
    );
  };

  const branchingMatches = matches.filter(m => m.match_type === 'branching');
  const peerMatches = matches.filter(m => m.match_type === 'peer');
  const careerMatches = matches.filter(m => m.match_score >= 50);
  // Featured: top 6 by match score (premium or highest scoring)
  const featuredMembers = [...matches]
    .sort((a, b) => (b.match_score || 0) - (a.match_score || 0))
    .slice(0, 6);

  const filteredBranching = filterBySearch(branchingMatches);
  const filteredPeers = filterBySearch(peerMatches);
  const filteredNear = filterBySearch(nearYou);
  const filteredCareer = filterBySearch(careerMatches);
  const filteredFeatured = filterBySearch(featuredMembers);

  return (
    <Layout>
      <div className="space-y-8" data-testid="explore-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit'] flex items-center gap-2">
              <Compass className="w-6 h-6 text-primary" /> Explore
            </h1>
            <p className="text-muted-foreground text-sm">Discover, connect, and grow your professional network</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search professionals..." className="pl-10 w-56" data-testid="explore-search" />
            </div>
            <Link to="/invite-contacts">
              <Button variant="outline" size="sm" data-testid="invite-contacts-btn"><UserPlus className="w-4 h-4 mr-1.5" />Import Contacts</Button>
            </Link>
          </div>
        </div>

        {/* Featured Members Spotlight */}
        <FeaturedSpotlight pros={filteredFeatured} onConnect={handleConnect} connecting={connecting} />

        {/* 1. Recommended for You */}
        <SectionRow
          title="Recommended for You"
          subtitle="AI-matched complementary professionals who grow your business"
          icon={Sparkles}
          iconColor="bg-primary/10 text-primary"
          pros={filteredBranching}
          onConnect={handleConnect}
          connecting={connecting}
          loading={loading}
          emptyMsg="Complete your profile to get smart recommendations"
        />

        {/* 2. Near You */}
        <SectionRow
          title="Near You"
          subtitle={user?.city ? `Professionals in ${user.city}` : 'Professionals in your area'}
          icon={MapPin}
          iconColor="bg-green-500/10 text-green-500"
          pros={filteredNear}
          onConnect={handleConnect}
          connecting={connecting}
          loading={loading}
          emptyMsg={`No professionals found near ${user?.city || 'your location'} yet`}
        />

        {/* 3. Career Match */}
        <SectionRow
          title="Career Match"
          subtitle="High-compatibility professionals for career growth and referrals"
          icon={Target}
          iconColor="bg-amber-500/10 text-amber-500"
          pros={filteredCareer}
          onConnect={handleConnect}
          connecting={connecting}
          loading={loading}
          emptyMsg="No career matches yet — complete your profile to unlock"
        />

        {/* 4. Peers Match */}
        <SectionRow
          title="Peers Match"
          subtitle="Same industry, different company — ideal referral partners"
          icon={Users}
          iconColor="bg-violet-500/10 text-violet-500"
          pros={filteredPeers}
          onConnect={handleConnect}
          connecting={connecting}
          loading={loading}
          emptyMsg="No peer matches found yet"
        />

        {/* 5. Local Business Discovery */}
        <section className="space-y-4" data-testid="section-local-discovery">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/10 text-emerald-500">
              <Filter className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-['Outfit']">Local Professionals</h2>
              <p className="text-xs text-muted-foreground">
                Browse by category {user?.city ? `in ${user.city}` : 'near you'}
              </p>
            </div>
            {localPros.length > 0 && <Badge variant="secondary" className="ml-auto text-xs">{localPros.length}</Badge>}
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border" data-testid="category-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id || 'all'}
                onClick={() => handleCategoryFilter(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid={`cat-${cat.id || 'all'}`}
              >
                <cat.icon className="w-3 h-3" />
                {cat.label}
              </button>
            ))}
          </div>

          {localLoading ? (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {[1,2,3,4].map(i => <div key={i} className="flex-shrink-0 w-72 h-72 bg-muted rounded-xl animate-pulse" />)}
            </div>
          ) : localPros.length > 0 ? (
            <ScrollableRow>
              {localPros.map(pro => (
                <HeroCard
                  key={pro.id}
                  pro={{
                    ...pro,
                    profession: pro.profession_name || pro.profession || 'Professional',
                    profile_photo: pro.profile_photo_url,
                  }}
                  onConnect={handleConnect}
                  connecting={connecting}
                />
              ))}
            </ScrollableRow>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <MapPin className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {activeCategory
                    ? `No ${CATEGORIES.find(c => c.id === activeCategory)?.label || 'professionals'} found near you yet`
                    : 'Complete your profile with city/state to discover local professionals'}
                </p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* 6. Groups for Business Owners */}
        <section className="space-y-4" data-testid="section-groups-for-business-owners">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-500">
              <Briefcase className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-['Outfit']">Groups for Business Owners</h2>
              <p className="text-xs text-muted-foreground">Join communities of professionals who share your goals</p>
            </div>
            {groups.length > 0 && <Badge variant="secondary" className="ml-auto text-xs">{groups.length}</Badge>}
          </div>
          {groups.length > 0 ? (
            <ScrollableRow>
              {groups.map(group => (
                <Link key={group.id} to="/groups" className="flex-shrink-0 w-72">
                  <Card className="overflow-hidden hover:shadow-lg transition-all h-full" data-testid={`group-card-${group.id}`}>
                    <div className="h-20 bg-gradient-to-br from-blue-500/30 to-primary/30 relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-2 left-3">
                        <Badge variant="secondary" className="bg-white/20 text-white text-[10px]">{group.member_count || 0} members</Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-sm line-clamp-1">{group.name}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{group.description || 'A professional networking community'}</p>
                      <Button size="sm" className="w-full mt-3 text-xs h-8" data-testid={`join-group-${group.id}`}>
                        <Users className="w-3 h-3 mr-1" />View Group
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </ScrollableRow>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <Briefcase className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No groups available yet. <Link to="/groups" className="text-primary hover:underline">Create one</Link></p>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </Layout>
  );
}
